import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import AmexExperientialEventsCard from './AmexExperientialEventsCard';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import { NEW_EVENTS, MAIN_EVENTS, PAST_EVENTS } from '../ExperientialEvents';
import AmexExperientialEventsFilter from './AmexExperientialEventsFilter';
import './AmexExperientialEvents.scss';

const AmexExperientialEvents = ({
  baseRequestUrl,
  eventsData = [],
  eventsConfig,
  newEventsConfig,
  pastEventsConfig,
  Analytics,
  isDesktop,
  isAuthenticated,
  $rootScope,
  selectedCoreSeats,
  updateParsedEventData,
  matchMedia,
  showAllEnabled,
  displaySection,
  handleFilterValue,
  eventsLocations,
  eventsMonths,
  isFilterEnabled,
  isFilterConfigEnabled,
}) => {
  let headerOne = '';
  let headerTwo = '';
  let sectionId = '';

  if (displaySection === NEW_EVENTS) {
    headerOne = newEventsConfig.title;
    sectionId = newEventsConfig.section_id;
  } else if (displaySection === PAST_EVENTS) {
    headerOne = pastEventsConfig.header_one;
    headerTwo = pastEventsConfig.header_two;
    sectionId = pastEventsConfig.section_id;
  } else {
    headerOne = eventsConfig.header_one;
    headerTwo = eventsConfig.header_two;
    sectionId = eventsConfig.section_id;
  }

  const [updatedEventData, setUpdatedEventData] = useState(null);
  const [errorAnnouncementNonce, setErrorAnnouncementNonce] = useState(0);
  const {
    soldout_label: soldoutLabel,
    no_events_message: noEventsMessage,
    no_events_message_for_filter: noEventsMsgForFilter,
    timezone_mappings: timezoneMappings,
    sold_out_message: soldOutMessage,
    display_venue_name: displayVenueName,
  } = eventsConfig;

  useEffect(() => {
    setUpdatedEventData(null);
  }, []);

  useEffect(() => {
    setUpdatedEventData(eventsData.slice(0));
  }, [eventsData]);

  useEffect(() => {
    setErrorAnnouncementNonce((nonce) => nonce + 1);
  }, [updatedEventData, isFilterEnabled]);

  const eventLength = updatedEventData?.length;
  const sanitizedHeaderOne = sanitizeString(headerOne);
  const sanitizedHeaderTwo = sanitizeString(headerTwo);

  const renderCards = () => {
    const cards = [];

    for (let i = 0; i < eventLength; i += 1) {
      cards.push((
        <AmexExperientialEventsCard
          key={updatedEventData[i].event_id}
          eventData={updatedEventData[i]}
          baseRequestUrl={baseRequestUrl}
          soldoutLabel={soldoutLabel}
          Analytics={Analytics}
          sectionName={sanitizedHeaderOne}
          isDesktop={isDesktop}
          timezoneMappings={timezoneMappings}
          isAuthenticated={isAuthenticated}
          $rootScope={$rootScope}
          selectedCoreSeats={selectedCoreSeats}
          updateParsedEventData={updateParsedEventData}
          soldOutMessage={soldOutMessage}
          matchMedia={matchMedia}
          showAllEnabled={showAllEnabled}
          displayVenueName={displayVenueName}
          displaySection={displaySection}
        />
      ));
    }
    return cards;
  };

  const showErrorAndStatusMessage = () => {
    // Toggle an invisible ZWSP so screen readers detect a text change and re-announce.
    const announcementSuffix = errorAnnouncementNonce % 2 === 1 ? '\u200B' : '';
    if (isFilterEnabled && eventLength === 0) {
      return (
        <div className="AmexExperientialEvents__notification-error">
          <p>{`${noEventsMsgForFilter}${announcementSuffix}`}</p>
        </div>
      );
    } if (eventLength === 0) {
      return (
        <div className="AmexExperientialEvents__notification-error">
          <p>{`${noEventsMessage}${announcementSuffix}`}</p>
        </div>
      );
    }

    return displaySection === MAIN_EVENTS && (
      <p className="sr-only">
        {`${eventLength} events.${announcementSuffix}`}
      </p>
    );
  };

  const renderEventsSection = () => (
    <ul className="AmexExperientialEvents__grid">
      {renderCards()}
    </ul>
  );

  return (
    <div
      className={cx('AmexExperientialEvents', { past: displaySection === PAST_EVENTS, new: displaySection === NEW_EVENTS })}
    >
      <div className="container">
        <h2
          id={sectionId}
          className="AmexExperientialEvents__title"
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: sanitizedHeaderOne,
          }}
        />
        {sanitizedHeaderTwo && (
        <p
          className="AmexExperientialEvents__intro"
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: sanitizedHeaderTwo,
          }}
        />
        )}
        {isFilterConfigEnabled && !showAllEnabled && (eventLength > 0 || isFilterEnabled) && (displaySection === MAIN_EVENTS)
          && (
          <AmexExperientialEventsFilter
            matchMedia={matchMedia}
            handleFilterValue={handleFilterValue}
            eventsLocations={eventsLocations}
            eventsMonths={eventsMonths}
          />
          )}
        <div className="AmexExperientialEvents__wrapper">
          <div role="status" aria-atomic="true">
            {showErrorAndStatusMessage()}
          </div>
          {renderEventsSection()}
        </div>
      </div>
    </div>
  );
};

AmexExperientialEvents.defaultProps = {
  displaySection: null,
  newEventsConfig: null,
  isFilterEnabled: false,
  pastEventsConfig: null,
  eventsLocations: null,
  eventsMonths: null,
  handleFilterValue: null,
  updateParsedEventData: null,
  isFilterConfigEnabled: false,
};

AmexExperientialEvents.propTypes = {
  baseRequestUrl: PropTypes.string.isRequired,
  eventsData: PropTypes.arrayOf(PropTypes.shape({
    event_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    event_name: PropTypes.string,
    event_type: PropTypes.number,
    image: PropTypes.string,
    logo: PropTypes.string,
    event_date_time: PropTypes.string,
    display_start_date_time: PropTypes.string,
    display_end_date_time: PropTypes.string,
    image_preview_text: PropTypes.shape({}),
    locations_text: PropTypes.string,
    sold_out: PropTypes.bool,
    event_url: PropTypes.string,
    time: PropTypes.string,
  })).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  newEventsConfig: PropTypes.shape({
    title: PropTypes.string,
    section_id: PropTypes.string,
  }),
  pastEventsConfig: PropTypes.shape({
    section_id: PropTypes.string,
    header_one: PropTypes.string,
    header_two: PropTypes.string,
  }),
  eventsConfig: PropTypes.shape({
    header_one: PropTypes.string,
    header_two: PropTypes.string,
    soldout_label: PropTypes.string,
    no_events_message: PropTypes.string,
    no_events_message_for_filter: PropTypes.string,
    section_id: PropTypes.string,
    timezone_mappings: PropTypes.shape({}),
    sold_out_message: PropTypes.string,
    display_venue_name: PropTypes.bool,
  }).isRequired,
  isDesktop: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func,
  }).isRequired,
  selectedCoreSeats: PropTypes.number.isRequired,
  updateParsedEventData: PropTypes.func,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    ipadLandscape: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  showAllEnabled: PropTypes.bool.isRequired,
  displaySection: PropTypes.string,
  handleFilterValue: PropTypes.func,
  eventsLocations: PropTypes.shape({}),
  eventsMonths: PropTypes.shape({}),
  isFilterEnabled: PropTypes.bool,
  isFilterConfigEnabled: PropTypes.bool,
};

export default AmexExperientialEvents;
